/**
 * The catalog filters that NARROW a keyword search — the classifier axis
 * (category / ИКПУ / niche), the region, the kind, the marketplace chips.
 *
 * Why this exists (2026-10-20, PostHog, 14 days): 584 keyword searches by
 * 305 people returned nothing, and 69 % of them ran with one of these filters
 * still on — a category picked on the home page stays in the URL while the
 * person types the next word, so «бумага» under «Медицина» finds 0 while the
 * whole catalogue holds 116. With a narrowing filter the empty rate is 29.9 %,
 * without one 6.8 %; 44 % of those people left the session without opening a
 * lot. The empty card now says what the UNFILTERED catalogue has and links to
 * it — nothing is changed silently, the person decides.
 */
export const NARROWING_PARAMS = [
  'category',
  'ikpu',
  'niche',
  'region',
  'kind',
  'source',
  'ifiSource',
] as const;

export type NarrowingParam = (typeof NARROWING_PARAMS)[number];

/** Which narrowing filters the current URL carries (empty when none). */
export function narrowingParamsIn(search: URLSearchParams): NarrowingParam[] {
  return NARROWING_PARAMS.filter((k) => {
    const v = search.get(k);
    return v !== null && v.trim() !== '';
  });
}

/**
 * The same page with every narrowing filter dropped (and `page` reset): the
 * keyword, status, scope, sort and the budget/deadline bounds stay — those
 * are not what hid the lots. Returns null when nothing would change.
 */
export function hrefWithoutNarrowing(pathname: string, search: URLSearchParams): string | null {
  if (narrowingParamsIn(search).length === 0) return null;
  const next = new URLSearchParams(search);
  for (const k of NARROWING_PARAMS) next.delete(k);
  next.delete('page');
  const qs = next.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/**
 * The same page searching the WHOLE catalogue (`features.searchScopeLine`): the word, the
 * sort and the bounds stay; every narrowing filter, a saved search's `alert` and the page
 * go. Returns null when no narrowing filter is on — then there is nothing to widen.
 * (`hrefWithoutNarrowing` keeps `alert`; a word search ignores it anyway, so dropping it
 * only keeps the address clean.)
 */
export function hrefSearchWholeCatalogue(pathname: string, search: URLSearchParams): string | null {
  if (narrowingParamsIn(search).length === 0) return null;
  const next = new URLSearchParams(search);
  for (const k of NARROWING_PARAMS) next.delete(k);
  next.delete('alert');
  next.delete('page');
  const qs = next.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
